import { LocationStrategy } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, Injectable, Injector } from "@angular/core";
import { ClientLogProvider } from "../../api/client-log/client-log.api";
import { AppConfig } from "../../app.config";
// FIXME: Sentryを外す
// import * as Sentry from "@sentry/browser";
import { Me } from "../../models/user";
import { Auth } from "../auth/auth.service";
import { ClientDiagnosticsService } from "../client-diagnostics/client-diagnostics.service";

@Injectable({
  providedIn: "root",
})
export class GlobalErrorHandler implements ErrorHandler {
  lastStackString = "";
  private auth: Auth;
  private clientLogProvider: ClientLogProvider;
  private location: LocationStrategy;
  private diagnostics: ClientDiagnosticsService;

  constructor(private injector: Injector) {
    setTimeout(() => {
      this.auth = this.injector.get(Auth);
      this.clientLogProvider = this.injector.get(ClientLogProvider);
      this.location = this.injector.get(LocationStrategy);
      this.diagnostics = this.injector.get(ClientDiagnosticsService);
    });
  }

  handleError(error: Error | HttpErrorResponse) {
    if (!error) {
      return;
    }
    const message = error.message ? error.message : error.toString();

    // アプリは HashLocationStrategy で動いているため、PathLocationStrategy に限定せず path() を取る
    // (以前は PathLocationStrategy のときだけ取得していたため url が常に空だった)
    let url: string;
    try {
      url = this.location?.path() ?? "";
    } catch (error) {
      url = "";
    }

    const user: Me = this.auth?.getMe() || new Me({});

    if (AppConfig.ENV === "production") {
      // sentry
      // Sentry.setTag("user_id", String(user.getId()));
      // const extractedError =
      //   this.extractError(error) || "Handled unknown error";
      // Sentry.captureException(extractedError);

      // どのモーダル/LPを開いていたか・直前の操作を添える(白画面などの原因調査用)
      const context = this.diagnostics?.buildContext() ?? null;
      const send = (stackString: string) => {
        // log on the server
        // stack が取れないエラー同士は message で連続重複を判定する
        const dedupeKey = stackString || message;
        if (this.lastStackString && this.lastStackString === dedupeKey) {
          return;
        }
        this.lastStackString = dedupeKey;
        if (
          message.indexOf("Firebase: The custom token format is incorrect.") !==
          -1
        ) {
          // FIXME: Firebaseのエラーは一旦外す
          return;
        }
        this.clientLogProvider.post({
          user_id: user.id,
          message: message,
          url: url,
          path: document.URL,
          stack: stackString,
          context: context ? JSON.stringify(context) : "",
        });
        this.diagnostics?.logErrorToAnalytics(message, context);
      };

      // stacktrace-jsはエラー発生時にしか使わないため遅延ロードし、初期バンドルから外す
      import("stacktrace-js")
        .then((module) => {
          // CJSモジュールのためdefault/名前空間どちらで解決されても動くようにする
          const StackTrace = module.default ?? module;
          // get the stack trace, lets grab the last 10 stacks only
          return StackTrace.fromError(error);
        })
        .then((stackframes) =>
          stackframes
            .splice(0, 20)
            .map((sf) => sf.toString())
            .join("\n")
        )
        // チャンク読込失敗などで stacktrace-js 自体を読めないときも、生の stack で記録は残す
        .catch(() => (error as Error).stack ?? "")
        .then((stackString) => send(stackString))
        .catch(() => {
          // 送信失敗時に再度handleErrorへ入る無限ループを防ぐため握りつぶす
        });
    } else {
      console.error(error);
    }
  }

  extractError(error) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === "string" || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === "string") {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Skip if there's no error, and let user decide what to do with it.
    return null;
  }
}
